/**
 * The "Players in your group" stepper (slice ② allocation, SIMPLIFIED model).
 *
 * ONE booking-level party-size count, applied to every court slot in the cart.
 * There is NO drag and NO per-court count — a single group size drives the price
 * (each court is priced for that count via the quote engine's `extra_player`
 * surcharge) and is capped by the tightest booked-court seat limit.
 *
 * The control is a `role="group"` (labelled "Players in your group") with a count
 * + a decrement and an increment button:
 *   - `−` ("Remove a player") floors the count at 1;
 *   - `+` ("Add a player") is DISABLED once the count reaches the cap, so the
 *     host can't even request an over-cap size from this control.
 *
 * It is purely presentational over a lifted count — the cap is computed by the
 * page from the booked courts (min of each court's max_players ?? facility
 * max_count), mirroring the backend's `_resolve_allocation` min() semantics. The
 * stepper never invents the cap or the price.
 *
 * NOTE: this is NOT the "Add players" INVITE cta — that invites people and does
 * not change the paid count. The labels here are deliberately the SINGULAR
 * "Add a player" / "Remove a player" so they never collide with the invite
 * control's "Add players".
 */
export function PlayerCountStepper({
  count,
  cap,
  onStep,
  disabled = false,
}: {
  count: number;
  /** Tightest booked-court cap; `+` disables once `count` reaches it. */
  cap: number;
  /**
   * Apply a +1 / −1 step. The PAGE owns the clamp via a functional state updater
   * (`setCount(prev => clamp(prev + delta))`) so a rapid click run is always
   * relative to the latest count — never a stale render closure. This is why the
   * stepper emits a DELTA, not an absolute next value.
   */
  onStep: (delta: 1 | -1) => void;
  disabled?: boolean;
}) {
  const atCap = count >= cap;
  const atFloor = count <= 1;

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2 }}>
      {/* The control itself is the labelled group. The cap announcement below is
          a SIBLING of this group (not a descendant) so the cap number never reads
          as part of the displayed group count — both for a screen reader and for
          tests that scope a count assertion to `within(group)`. */}
      <Stack role="group" aria-label="Players in your group" spacing={1.5}>
        <Stack spacing={0.25}>
          <Typography sx={{ fontWeight: 700 }}>
            Players in your group
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Applies to every court — the price updates as you add players.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <IconButton
            type="button"
            aria-label="Remove a player"
            onClick={() => onStep(-1)}
            disabled={disabled || atFloor}
            sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
          >
            <RemoveRounded />
          </IconButton>
          <Typography
            aria-live="polite"
            variant="h5"
            sx={{ minWidth: 32, textAlign: "center", fontWeight: 800 }}
          >
            {count}
          </Typography>
          <IconButton
            type="button"
            aria-label="Add a player"
            onClick={() => onStep(1)}
            disabled={disabled || atCap}
            sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
          >
            <AddRounded />
          </IconButton>
        </Stack>
      </Stack>
      {/* When the count hits the cap the "+" silently disables — announce the
          limit so a screen-reader user who hits it understands WHY, instead of a
          mute dead button. Only rendered at the cap (a live region that is empty
          otherwise stays silent). */}
      {atCap ? (
        <Typography
          variant="caption"
          color="text.secondary"
          role="status"
          sx={{ display: "block", mt: 1 }}
        >
          Maximum {cap} {cap === 1 ? "player" : "players"} for these courts
        </Typography>
      ) : null}
    </Paper>
  );
}
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
